import React, { useRef, useState } from 'react';
import { usePortalContext } from '@/layouts/CustomerPortalLayout';
import { formatDate, formatRelativeTime } from '@/utils/formatters';
import {
  FileText, Download, Upload, Inbox, Check, Send, Package, Star,
  CheckCircle2, Clock, Activity, Phone, File, FileCheck
} from 'lucide-react';

const iconMap = {
  inbox: Inbox, check: Check, send: Send, package: Package, star: Star,
  'check-circle': CheckCircle2, phone: Phone,
};

const documentTypeLabels = {
  angebot: 'Angebot',
  vertrag: 'Vertrag',
  vollmacht: 'Vollmacht',
  lastgang: 'Lastgang',
  lastgang_report: 'Verbrauchsanalyse',
  kuendigung: 'Kuendigungsschreiben',
  custom: 'Dokument',
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const PortalDocuments = () => {
  const { data } = usePortalContext();
  const { lead, timeline, documents, offers } = data;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    // Placeholder — would POST to /api/public/portal/:token/upload-document
    setTimeout(() => setUploading(false), 1000);
  };

  // Checklist of things the customer should do
  const checklist = [
    { label: 'Verbrauchsdaten bestaetigt', done: !!data.company?.annual_consumption_kwh },
    { label: 'Kontaktdaten verifiziert', done: !!data.contact?.email },
    { label: 'Lastgang hochgeladen', done: documents.some(d => d.document_type === 'lastgang' || d.document_type === 'lastgang_report'), optional: true },
    { label: 'Angebote eingesehen', done: offers.some(o => o.total_views > 0) },
    { label: 'Angebot ausgewaehlt', done: offers.some(o => o.status === 'angenommen') },
    { label: 'Vollmacht unterzeichnet', done: documents.some(d => d.document_type === 'vollmacht' && d.uploaded_by_customer), requires: 'Nach Angebotsauswahl' },
  ];

  const completionPercent = Math.round(checklist.filter(c => c.done || c.optional).length / checklist.length * 100);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground tracking-tight">Dokumente &amp; Status</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Alles was wir gemeinsam erarbeiten an einem Ort — transparent und nachvollziehbar.
        </p>
      </div>

      {/* Progress checklist */}
      <div className="hk-card">
        <div className="hk-card-header">
          <h6>Was wir von Ihnen brauchen</h6>
          <span className="hk-badge primary">{completionPercent}% erledigt</span>
        </div>
        <div className="hk-card-body">
          <div className="space-y-3">
            {checklist.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  item.done
                    ? 'bg-success text-white'
                    : 'bg-muted border-2 border-border'
                }`}>
                  {item.done && <Check size={14} />}
                </div>
                <div className="flex-1">
                  <span className={`text-sm ${item.done ? 'text-muted-foreground line-through' : 'text-foreground font-semibold'}`}>
                    {item.label}
                  </span>
                  {item.optional && !item.done && <span className="ml-2 text-[10px] text-muted-foreground">(optional)</span>}
                  {item.requires && !item.done && <span className="ml-2 text-[10px] text-muted-foreground">{item.requires}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main grid: Documents + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Documents */}
        <div className="hk-card">
          <div className="hk-card-header">
            <h6>Ihre Dokumente</h6>
            <span className="text-xs text-muted-foreground">{documents.length} Dateien</span>
          </div>
          <div className="hk-card-body">
            {documents.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="mx-auto text-muted-foreground mb-2" size={32} />
                <p className="text-sm text-muted-foreground">Noch keine Dokumente</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group">
                    <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileCheck className="text-primary" size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{doc.file_name}</p>
                        {doc.uploaded_by_customer && <span className="text-[9px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded uppercase font-bold">Hochgeladen</span>}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{documentTypeLabels[doc.document_type] || 'Dokument'}</span>
                        <span>&middot;</span>
                        <span>{formatFileSize(doc.file_size_bytes)}</span>
                        <span>&middot;</span>
                        <span>{formatDate(doc.created_at)}</span>
                      </div>
                    </div>
                    <button className="p-2 text-muted-foreground hover:text-primary rounded-lg hover:bg-accent opacity-0 group-hover:opacity-100 transition-all">
                      <Download size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload area */}
            <div className="mt-4 pt-4 border-t border-border">
              <button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-4 text-sm font-semibold text-muted-foreground hover:border-primary hover:text-primary hover:bg-accent transition-all"
              >
                <Upload size={16} />
                {uploading ? 'Wird hochgeladen...' : 'Dokument hochladen (Lastgang, Rechnung, Vertrag...)'}
              </button>
              <input ref={inputRef} type="file" onChange={handleUpload} className="hidden" accept=".pdf,.csv,.xlsx,.xls,.jpg,.png" />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="hk-card">
          <div className="hk-card-header">
            <h6>Prozess-Historie</h6>
            <span className="text-xs text-muted-foreground">{timeline.length} Ereignisse</span>
          </div>
          <div className="hk-card-body">
            {timeline.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="mx-auto text-muted-foreground mb-2" size={32} />
                <p className="text-sm text-muted-foreground">Noch keine Ereignisse</p>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-border" />
                <div className="space-y-5">
                  {timeline.map((event) => {
                    const Icon = iconMap[event.icon] || Activity;
                    return (
                      <div key={event.id} className="relative flex items-start gap-4">
                        <div className="relative z-10 w-10 h-10 rounded-full bg-accent border-2 border-card flex items-center justify-center flex-shrink-0">
                          <Icon size={16} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0 pt-1.5">
                          <p className="text-sm font-semibold text-foreground">{event.title}</p>
                          {event.description && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{event.description}</p>}
                          <p className="text-[11px] text-muted-foreground mt-1.5">{formatRelativeTime(event.created_at)} &middot; {formatDate(event.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="hk-card" style={{ background: 'linear-gradient(135deg, #e6f2f3 0%, #ffffff 100%)', borderColor: '#007D88' }}>
        <div className="hk-card-header" style={{ borderBottomColor: 'rgba(0, 125, 136, 0.15)' }}>
          <h6>Was passiert als Naechstes?</h6>
          <Clock size={14} className="text-primary" />
        </div>
        <div className="hk-card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold mb-2">1</div>
              <p className="text-sm font-bold text-foreground">Sie waehlen ein Angebot</p>
              <p className="text-xs text-muted-foreground mt-1">Im Bereich Angebote finden Sie alle Optionen. Nach der Auswahl bereiten wir die Unterlagen vor.</p>
            </div>
            <div>
              <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold mb-2">2</div>
              <p className="text-sm font-bold text-foreground">Wir schicken Vollmacht + Vertrag</p>
              <p className="text-xs text-muted-foreground mt-1">Innerhalb von 24 Stunden nach Ihrer Auswahl erhalten Sie alle Dokumente zur Unterschrift.</p>
            </div>
            <div>
              <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold mb-2">3</div>
              <p className="text-sm font-bold text-foreground">Wir kuemmern uns um alles</p>
              <p className="text-xs text-muted-foreground mt-1">Wir uebernehmen den Lieferantenwechsel und die Kuendigung Ihres alten Vertrags. Kein Aufwand fuer Sie.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalDocuments;
